"use client";
import { useState } from "react";
import { contactContent } from "@/lib/content";
import { fetchContactToken } from "@/lib/contact/token-client";

type Status = "idle" | "sending" | "sent" | "error";

/**
 * One field, one request. It posts to the same endpoint as the contact form with a
 * source attached, so a signup from here is distinguishable in the inbox from one made
 * inside the tool.
 */
export function SignupForm() {
  const t = contactContent.access;
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState<Status>("idle");

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!email.trim() || status === "sending") return;
    setStatus("sending");

    const token = await fetchContactToken();
    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type: "sign_up",
          source: "early_access",
          email: email.trim(),
          token,
        }),
      });
      setStatus(res.ok ? "sent" : "error");
      if (res.ok) setEmail("");
    } catch {
      setStatus("error");
    }
  }

  if (status === "sent") {
    return <p className="rounded-md border border-ok/30 bg-ok-soft px-5 py-4 text-[14px] text-ok">{t.sent}</p>;
  }

  return (
    <form onSubmit={onSubmit} className="grid gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <input
          type="email"
          required
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (status === "error") setStatus("idle");
          }}
          placeholder={t.placeholder}
          autoComplete="email"
          className="min-w-0 flex-1 rounded-md border border-line bg-bg px-3.5 py-2.5 text-[14px] outline-none transition-colors placeholder:text-fg-faint focus:border-fg-muted"
        />
        <button
          type="submit"
          disabled={status === "sending"}
          className="cursor-pointer rounded-full bg-btn px-5 py-2.5 text-[14px] text-btn-fg transition-opacity hover:opacity-90 disabled:cursor-wait disabled:opacity-60"
        >
          {status === "sending" ? `${t.sending}...` : t.submit}
        </button>
      </div>
      {status === "error" && (
        <p role="alert" className="text-[13px] text-accent">
          {t.error}
        </p>
      )}
      <p className="text-[12.5px] text-fg-faint">{t.note}</p>
    </form>
  );
}
